// client/src/components/stats/StatRow.tsx
//
// Componente reutilizable para mostrar una fila de stat con su rango saludable,
// evaluación de leak (ok/warn/critical) y un gap indicator.
//
// Props nuevas vs el componente original:
//   - expandable?: boolean — si es true, muestra un botón para expandir
//   - onToggleExpand?: () => void
//   - isExpanded?: boolean
//   - lowSample?: boolean — muestra un indicador "n bajo"
//   - sampleSize?: number — muestra "(n=X)" al lado del valor

import React from 'react';
import { StackKey } from './leakRanges';
import { useBenchmarks } from '../../contexts/BenchmarkContext';

export interface StatRowProps {
  label: string;
  value: number;
  statKey: string;
  activeStack: StackKey;
  unit?: string;
  sampleSize?: number;
  lowSample?: boolean;
  expandable?: boolean;
  isExpanded?: boolean;
  onToggleExpand?: () => void;
  children?: React.ReactNode; // para contenido expandido abajo
  indent?: boolean; // para sub-filas (ej. los splits de fold-to-3bet)
}

export function StatRow({
  label,
  value,
  statKey,
  activeStack,
  unit = '%',
  sampleSize,
  lowSample = false,
  expandable = false,
  isExpanded = false,
  onToggleExpand,
  children,
  indent = false,
}: StatRowProps) {
  const bm = useBenchmarks();
  const status = lowSample ? 'ok' : bm.evalStat(statKey, value, activeStack);
  const r = bm.getRange(statKey, activeStack);

  const rowClass = [
    'stat-row',
    `stat-${status}`,
    expandable ? 'stat-row-expandable' : '',
    indent ? 'stat-row-indent' : '',
    lowSample ? 'stat-row-low-sample' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div
        className={rowClass}
        onClick={expandable ? onToggleExpand : undefined}
        style={expandable ? { cursor: 'pointer' } : undefined}
      >
        {expandable && (
          <span className="stat-expand-caret" aria-hidden>
            {isExpanded ? '▾' : '▸'}
          </span>
        )}
        <span className="stat-label">{label}</span>
        <span className="stat-value">
          {value}
          {unit}
          {sampleSize !== undefined && (
            <span className="stat-sample" style={{ marginLeft: 6, opacity: 0.6, fontSize: '0.85em' }}>
              (n={sampleSize})
            </span>
          )}
        </span>
        {r && !lowSample && (
          <span className="stat-range">
            opt: {r.min}–{r.max}
            {unit}
            {status !== 'ok' && (
              <span className="stat-gap">
                {value > r.max
                  ? ` ▲+${(value - r.max).toFixed(1)}`
                  : ` ▼-${(r.min - value).toFixed(1)}`}
              </span>
            )}
          </span>
        )}
        {lowSample && <span className="stat-range stat-low-sample-badge">insufficient sample</span>}
        <span className={`stat-dot dot-${status}`} />
      </div>
      {isExpanded && children}
    </>
  );
}
